export function expandPowers(expression) {
  return expression.replace(/x\^(\d+)/g, (_, exp) =>
    Array(parseInt(exp)).fill('x').join('*')
  );
}

const KNOWN_FUNCTIONS = new Set([
  'sin',
  'cos',
  'tan',
  'cot',
  'asin',
  'acos',
  'atan',
  'sqrt',
  'log',
  'ln',
  'abs',
  'exp',
]);

function normalizeFunctionAliases(expression) {
  return String(expression || '')
    .replace(/π/g, 'pi')
    .replace(/√\s*\(/g, 'sqrt(')
    .replace(/\btg\s*\(/gi, 'tan(')
    .replace(/\bctg\s*\(/gi, 'cot(')
    .replace(/\bln\s*\(/gi, 'log(');
}

function normalizeBareFunctionArguments(expression) {
  let result = String(expression || '');
  const names = ['asin', 'acos', 'atan', 'sqrt', 'sin', 'cos', 'tan', 'cot', 'log', 'abs', 'exp'];

  names.forEach((name) => {
    const bareX = new RegExp(`\\b${name}\\s*x\\b`, 'gi');
    result = result.replace(bareX, `${name}(x)`);

    const spacedSymbol = new RegExp(`\\b${name}\\s+([a-zA-Z])\\b`, 'gi');
    result = result.replace(spacedSymbol, `${name}($1)`);
  });

  return result;
}

function insertImplicitMultiplication(expression) {
  let result = String(expression || '');
  result = result.replace(/(\d)([a-zA-Z])/g, '$1*$2');
  result = result.replace(/\)([a-zA-Z])/g, ')*$1');
  result = result.replace(/([a-zA-Z])\(/g, (match, name, offset, source) => {
    let start = offset;
    while (start > 0 && /[a-zA-Z]/.test(source[start - 1])) start -= 1;
    const word = source.slice(start, offset + 1).toLowerCase();
    return KNOWN_FUNCTIONS.has(word) ? match : `${name}*(`;
  });
  return result;
}

function topLevelCommaIndex(value) {
  let depth = 0;
  for (let i = 0; i < value.length; i += 1) {
    const ch = value[i];
    if (ch === '(') depth += 1;
    else if (ch === ')') depth -= 1;
    else if (ch === ',' && depth === 0) return i;
  }
  return -1;
}

// log(podstawa, argument) -> (log(argument)/log(podstawa))
// mathjs domyślnie czyta log(a, b) jako log_b(a), co dawało bezsensowny wykres.
// Tu wymuszamy notację "podstawa, argument" zamienioną na iloraz logarytmów
// naturalnych, działającą dla podstaw całkowitych i ułamkowych (np. log(1/2, x)).
function convertLogBase(expression) {
  let result = String(expression || '');
  let searchFrom = 0;
  let guard = 0;
  while (guard < 100) {
    guard += 1;
    const idx = result.toLowerCase().indexOf('log(', searchFrom);
    if (idx < 0) break;
    const openIndex = idx + 3;
    const closeIndex = findMatchingRightParen(result, openIndex);
    if (closeIndex < 0) break;
    const inside = result.slice(openIndex + 1, closeIndex);
    const commaIndex = topLevelCommaIndex(inside);
    if (commaIndex < 0) {
      searchFrom = openIndex + 1;
      continue;
    }
    const base = inside.slice(0, commaIndex).trim();
    const arg = inside.slice(commaIndex + 1).trim();
    const replacement = `(log(${arg})/log(${base}))`;
    result = result.slice(0, idx) + replacement + result.slice(closeIndex + 1);
    searchFrom = idx + replacement.length;
  }
  return result;
}

// Wyciąga argumenty (drugą część) wywołań log(podstawa, argument).
// Używane do wyznaczenia pionowych asymptot logarytmu (miejsca zerowe argumentu).
export function extractLogArguments(equation) {
  const aliased = normalizeFunctionAliases(equation);
  const args = [];
  let searchFrom = 0;
  let guard = 0;
  while (guard < 50) {
    guard += 1;
    const idx = aliased.toLowerCase().indexOf('log(', searchFrom);
    if (idx < 0) break;
    const openIndex = idx + 3;
    const closeIndex = findMatchingRightParen(aliased, openIndex);
    if (closeIndex < 0) break;
    const inside = aliased.slice(openIndex + 1, closeIndex);
    const commaIndex = topLevelCommaIndex(inside);
    const arg = (commaIndex >= 0 ? inside.slice(commaIndex + 1) : inside).trim();
    if (arg) args.push(arg);
    searchFrom = closeIndex + 1;
  }
  return args;
}

export function sanitizeExpression(equation) {
  const aliased = normalizeFunctionAliases(equation);
  const withLogBase = convertLogBase(aliased);
  const normalized = normalizeBareFunctionArguments(withLogBase);
  const expanded = expandPowers(normalized);
  return insertImplicitMultiplication(expanded);
}

function findMatchingLeftParen(value, rightIndex) {
  let depth = 0;
  for (let i = rightIndex; i >= 0; i -= 1) {
    if (value[i] === ')') depth += 1;
    if (value[i] === '(') depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
}

function findMatchingRightParen(value, leftIndex) {
  let depth = 0;
  for (let i = leftIndex; i < value.length; i += 1) {
    if (value[i] === '(') depth += 1;
    if (value[i] === ')') depth -= 1;
    if (depth === 0) return i;
  }
  return -1;
}

function isWrapped(value) {
  if (!value.startsWith('(') || !value.endsWith(')')) return false;
  return findMatchingRightParen(value, 0) === value.length - 1;
}

function unwrap(value) {
  let current = value.trim();
  while (isWrapped(current)) {
    current = current.slice(1, -1).trim();
  }
  return current;
}

function previousFactorStart(value, slashIndex) {
  let i = slashIndex - 1;
  while (i >= 0 && /\s/.test(value[i])) i -= 1;
  if (value[i] === ')') return findMatchingLeftParen(value, i);
  while (i >= 0 && !/[+\-*/]/.test(value[i])) i -= 1;
  return i + 1;
}

function nextFactorEnd(value, slashIndex) {
  let i = slashIndex + 1;
  while (i < value.length && /\s/.test(value[i])) i += 1;
  if (value[i] === '(') {
    const end = findMatchingRightParen(value, i);
    return end >= 0 ? end + 1 : value.length;
  }
  while (i < value.length && !/[+\-*/]/.test(value[i])) i += 1;
  return i;
}

function replaceDivisionsWithLatex(value) {
  let result = value;
  let slashIndex = result.indexOf('/');
  while (slashIndex >= 0) {
    const leftStart = previousFactorStart(result, slashIndex);
    const rightEnd = nextFactorEnd(result, slashIndex);
    if (leftStart < 0 || rightEnd <= slashIndex + 1) break;
    const numerator = unwrap(result.slice(leftStart, slashIndex));
    const denominator = unwrap(result.slice(slashIndex + 1, rightEnd));
    result = `${result.slice(0, leftStart)}\\frac{${numerator}}{${denominator}}${result.slice(rightEnd)}`;
    slashIndex = result.indexOf('/', leftStart + 6);
  }
  return result;
}

function replaceSqrtWithLatex(value) {
  let result = String(value || '');
  let sqrtIndex = result.search(/(?:sqrt|√)\s*\(/i);
  let guard = 0;
  while (sqrtIndex >= 0 && guard < 20) {
    const openIndex = result.indexOf('(', sqrtIndex);
    const closeIndex = findMatchingRightParen(result, openIndex);
    if (openIndex < 0 || closeIndex < 0) break;
    const inside = result.slice(openIndex + 1, closeIndex);
    result = `${result.slice(0, sqrtIndex)}\\sqrt{${inside}}${result.slice(closeIndex + 1)}`;
    sqrtIndex = result.search(/(?:sqrt|√)\s*\(/i);
    guard += 1;
  }
  return result;
}

// log(podstawa, argument) -> \log_{podstawa}(argument); podstawa z własnym ułamkiem
function replaceLogBaseWithLatex(value) {
  let result = String(value || '');
  let searchFrom = 0;
  let guard = 0;
  while (guard < 50) {
    guard += 1;
    const idx = result.toLowerCase().indexOf('log(', searchFrom);
    if (idx < 0) break;
    const openIndex = idx + 3;
    const closeIndex = findMatchingRightParen(result, openIndex);
    if (closeIndex < 0) break;
    const inside = result.slice(openIndex + 1, closeIndex);
    const commaIndex = topLevelCommaIndex(inside);
    if (commaIndex < 0) {
      searchFrom = openIndex + 1;
      continue;
    }
    const base = inside.slice(0, commaIndex).trim();
    const arg = inside.slice(commaIndex + 1).trim();
    const baseLatex = replaceDivisionsWithLatex(base);
    const replacement = `\\log_{${baseLatex}}(${arg})`;
    result = result.slice(0, idx) + replacement + result.slice(closeIndex + 1);
    searchFrom = idx + replacement.length;
  }
  return result;
}

export function toLatexFraction(expr) {
  const [lhs, rhs] = expr.split('=');
  if (!rhs) {
    return replaceSqrtWithLatex(replaceLogBaseWithLatex(expr))
      .replace(/π/g, '\\pi')
      .replace(/\^/g, '^');
  }
  const latexRhs = replaceSqrtWithLatex(replaceDivisionsWithLatex(replaceLogBaseWithLatex(rhs)))
    .replace(/π/g, '\\pi')
    .replace(/\^/g, '^');
  return `${lhs.trim()} = ${latexRhs}`;
}
